import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/mock';

// Base URL for the site - use production URL for SEO schemas (required for structured data)
const SITE_URL = 'https://lerosebif.fr';
const LOGO_URL = 'https://lerosebif.fr/logo.png';
export const BUSINESS_ID = `${SITE_URL}/#business`;
const BUSINESS_REF = { "@type": "LocalBusiness", "@id": BUSINESS_ID, "name": "Le Rosebif - Jardinier à domicile" };

// LocalBusiness Schema for the main business
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    "name": "Le Rosebif - Jardinier à domicile",
    "alternateName": "Le Rosebif",
    "description": "Jardinier à domicile basé à Coëx (85220) en Vendée, dans un rayon d'environ 30 km. Service à la Personne (SAP813136249) : crédit d'impôt de 50 % pour les clients éligibles. Tonte, taille de haies, débroussaillage, désherbage, rénovation de pelouse existante.",
    "url": SITE_URL,
    "telephone": "+33782586685",
    "email": siteConfig.contact.email,
    "image": LOGO_URL,
    "logo": LOGO_URL,
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Virement bancaire, Chèque",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Coëx",
      "addressLocality": "Coëx",
      "addressRegion": "Vendée",
      "postalCode": "85220",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 46.6983,
      "longitude": -1.7633
    },
    "areaServed": [
      {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 46.6983,
          "longitude": -1.7633
        },
        "geoRadius": "30000"
      },
      ...(siteConfig.serviceArea?.cities || []).map(city => ({
        "@type": "City",
        "name": city,
        "containedInPlace": {
          "@type": "AdministrativeArea",
          "name": "Vendée"
        }
      }))
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Thursday", "Friday"],
        "opens": "11:00",
        "closes": "17:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services de jardinage",
      "itemListElement": siteConfig.services.map((service) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        }
      }))
    },
    "sameAs": [
      "https://www.facebook.com/lerosebif",
      "https://maps.app.goo.gl/3ssm3YBHfBfcYC8s5"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema - supports both {question, answer} and {q, a} formats
// Filters out invalid entries to prevent Google Search Console errors
export const FAQSchema = ({ faqs }) => {
  // Filter and validate FAQ entries - only include items with both question AND answer
  const validFaqs = (faqs || []).filter(faq => {
    const question = faq?.question || faq?.q;
    const answer = faq?.answer || faq?.a;
    return question && answer && typeof question === 'string' && typeof answer === 'string' && question.trim() && answer.trim();
  });

  // Don't render schema if no valid FAQs
  if (validFaqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": validFaqs.map(faq => ({
      "@type": "Question",
      "name": (faq.question || faq.q).trim(),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": (faq.answer || faq.a).trim()
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Page Schema specifically for location pages with full content
// Filters out invalid entries to prevent Google Search Console errors
export const LocationFAQSchema = ({ faqs, cityName }) => {
  // Filter and validate FAQ entries - only include items with both question AND answer
  const validFaqs = (faqs || []).filter(faq => {
    const question = faq?.question || faq?.q;
    const answer = faq?.answer || faq?.a;
    return question && answer && typeof question === 'string' && typeof answer === 'string' && question.trim() && answer.trim();
  });

  // Don't render schema if no valid FAQs
  if (validFaqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": `Questions fréquentes - Jardinage à ${cityName}`,
    "mainEntity": validFaqs.map(faq => ({
      "@type": "Question",
      "name": (faq.question || faq.q).trim(),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": (faq.answer || faq.a).trim()
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema
export const ServiceSchema = ({ service, location = null }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": service.title,
    "name": location 
      ? `${service.title} à ${location}` 
      : `${service.title} - Le Rosebif`,
    "description": service.description,
    "provider": BUSINESS_REF,
    "areaServed": service.areaServed ? service.areaServed.map((name) => ({ "@type": "City", "name": name })) : location ? {
      "@type": "City",
      "name": location,
      "containedInPlace": {
        "@type": "State",
        "name": "Vendée"
      }
    } : {
      "@type": "State",
      "name": "Vendée"
    },
    "offers": {
      "@type": "Offer",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "EUR",
        "description": "Prix TTC - Crédit d'impôt 50% applicable"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Organization Schema (for homepage)
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": BUSINESS_ID,
    "name": "Le Rosebif",
    "url": SITE_URL,
    "logo": LOGO_URL,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": siteConfig.contact.phone,
      "contactType": "customer service",
      "availableLanguage": "French",
      "areaServed": "FR"
    },
    "identifier": siteConfig.contact.siret,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Coëx",
      "addressRegion": "Vendée",
      "postalCode": "85220",
      "addressCountry": "FR"
    },
    "sameAs": [
      "https://www.facebook.com/lerosebif",
      "https://maps.app.goo.gl/3ssm3YBHfBfcYC8s5"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebSite Schema with SearchAction
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "name": "Le Rosebif - Jardinier à domicile en Vendée",
    "alternateName": "Le Rosebif Jardinage",
    "url": SITE_URL,
    "description": "Service de jardinage professionnel à Coëx et en Vendée. Crédit d'impôt 50%.",
    "publisher": { "@id": BUSINESS_ID },
    "inLanguage": "fr-FR"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// SEO Head Component for consistent meta tags
export const SEOHead = ({ 
  title, 
  description, 
  canonical,
  type = "website",
  image = LOGO_URL,
  ogImage = null,
  cityName = null
}) => {
  const fullTitle = title.includes('Le Rosebif') ? title : `${title} | Le Rosebif`;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const geoPlacename = cityName ? `${cityName}, Vendée` : "Coëx, Vendée";
  // Prefer the page-specific ogImage when provided (dynamic per service+town).
  const finalOgImage = ogImage || image;
  
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="Le Rosebif" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalOgImage} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="geo.region" content="FR-85" />
      <meta name="geo.placename" content={geoPlacename} />
      <meta name="geo.position" content="46.6983;-1.7633" />
      <meta name="ICBM" content="46.6983, -1.7633" />
    </Helmet>
  );
};

// Location Page Schema
export const LocationPageSchema = ({ cityName, citySlug }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/zone/${citySlug}#service`,
    "name": `Jardinier à domicile à ${cityName}`,
    "serviceType": "Entretien de jardin à domicile",
    "description": `Entretien de jardin à ${cityName} (Vendée) par Le Rosebif, jardinier basé à Coëx : tonte, taille de haies, débroussaillage. Service à la Personne, crédit d'impôt de 50 % pour les clients éligibles.`,
    "url": `${SITE_URL}/zone/${citySlug}`,
    "provider": BUSINESS_REF,
    "areaServed": {
      "@type": "City",
      "name": cityName,
      "containedInPlace": { "@type": "AdministrativeArea", "name": "Vendée" }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `Services de jardinage à ${cityName}`,
      "itemListElement": siteConfig.services.map((service) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": `${service.title} à ${cityName}`,
          "description": service.description
        }
      }))
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Article Schema for guide pages
export const ArticleSchema = ({ title, description, url, datePublished, dateModified }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": `${SITE_URL}${url}`,
    "datePublished": datePublished || "2024-01-01",
    "dateModified": dateModified || datePublished || "2024-01-01",
    "author": { "@type": "Organization", "@id": BUSINESS_ID, "name": "Le Rosebif", "url": SITE_URL },
    "publisher": {
      "@type": "Organization",
      "@id": BUSINESS_ID,
      "name": "Le Rosebif",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}${url}`
    },
    "image": LOGO_URL,
    "inLanguage": "fr-FR"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Product Schema for services with pricing
export const ProductSchema = ({ name, description, price, priceAfterCredit }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "brand": {
      "@type": "Brand",
      "name": "Le Rosebif"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "EUR",
      "price": price,
      "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "Le Rosebif"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// HowTo Schema for guide sections
export const HowToSchema = ({ name, description, steps }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "totalTime": "PT30M",
    "tool": [
      { "@type": "HowToTool", "name": "Tondeuse" },
      { "@type": "HowToTool", "name": "Taille-haie" },
      { "@type": "HowToTool", "name": "Débroussailleuse" }
    ],
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      "url": step.url || `${SITE_URL}/guide-jardinage-vendee#step-${index + 1}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default {
  LocalBusinessSchema,
  FAQSchema,
  LocationFAQSchema,
  ServiceSchema,
  BreadcrumbSchema,
  OrganizationSchema,
  WebsiteSchema,
  SEOHead,
  LocationPageSchema,
  ArticleSchema,
  ProductSchema,
  HowToSchema
};
